import * as React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DataGrid } from '@mui/x-data-grid';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ruRU } from '@mui/x-data-grid/locales';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { logout } from './nuiCommon/nuiActions';
import axios from 'axios';

const theme = createTheme(ruRU);

const OUTCOME_LABEL = {
  answered:  { text: 'ответил',    color: 'success' },
  escalated: { text: 'эскалация',  color: 'warning' },
  limited:   { text: 'лимит',      color: 'error'   },
  greeted:   { text: 'приветствие', color: 'default' },
  explained: { text: 'о себе',      color: 'default' }
};

function outcomeChip(outcome) {
  const o = OUTCOME_LABEL[outcome] || { text: outcome, color: 'default' };
  return <Chip size="small" label={o.text} color={o.color} variant="outlined"/>;
}

/** Плоский список реплик -> диалоги. Сервер отдаёт сообщения обеих ролей
    подряд; строка таблицы - разговор, а не реплика. */
function toDialogs(messages) {
  const byId = new Map();
  messages.forEach((m) => {
    if (!byId.has(m.dialogId)) byId.set(m.dialogId, []);
    byId.get(m.dialogId).push(m);
  });
  return Array.from(byId, ([id, msgs]) => {
    const answers = msgs.filter((m) => m.role === 'assistant');
    const asked = msgs.find((m) => m.role === 'user');
    const tops = answers.map((m) => m.top1).filter((t) => t != null);
    // Пользователь у диалога один; берём его с первой реплики, где он есть -
    // анонимный разговор так и остаётся без имени.
    const known = msgs.find((m) => m.userId != null);
    return {
      id,
      messages: msgs,
      started: msgs[0].date,
      product: msgs[0].product,
      lang: msgs[0].lang,
      turns: msgs.length,
      // Исход разговора - исход последнего ответа: им он и закончился.
      outcome: answers.length ? answers[answers.length - 1].outcome : null,
      top1: tops.length ? Math.max(...tops) : null,
      attachments: msgs.some((m) => m.attachments),
      login: known ? known.login : '',
      userName: known ? known.userName : '',
      userEmail: known ? known.userEmail : '',
      question: asked ? asked.message : ''
    };
  });
}

const columns = [
  { field: 'started', headerName: 'Начало', width: 150,
    valueGetter: (v) => dayjs(v).format('DD.MM.YYYY HH:mm') },
  { field: 'product', headerName: 'Продукт', width: 130 },
  { field: 'login', headerName: 'Пользователь', width: 140,
    // Пустая ячейка - это не потеря данных, а анонимный посетитель витрины.
    valueGetter: (v) => v || '' },
  { field: 'turns', headerName: 'Реплик', width: 80, type: 'number' },
  { field: 'outcome', headerName: 'Исход', width: 120,
    renderCell: (params) => params.value ? outcomeChip(params.value) : null },
  { field: 'top1', headerName: 'top1', width: 80,
    valueGetter: (v) => v == null ? '' : v.toFixed(3) },
  { field: 'question', headerName: 'Первый вопрос', flex: 1, minWidth: 220 }
];

function Transcript({ dialog, onDelete }) {
  if (!dialog) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        Выберите диалог слева.
      </Typography>);
  }
  return (
    <Box sx={{ p: 2, overflowY: 'auto', maxHeight: 560 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
             sx={{ mb: 1 }}>
        <Box>
          <Typography variant="subtitle2">
            {dialog.id} &middot; {dialog.product} &middot; {dialog.lang}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dialog.login
             ? [dialog.login, dialog.userName, dialog.userEmail]
                 .filter((x) => x).join(' \u00b7 ')
             : 'не залогинен'}
          </Typography>
        </Box>
        <Button size="small" color="error" variant="outlined"
                startIcon={<DeleteOutlineIcon/>}
                onClick={onDelete}>
          Удалить
        </Button>
      </Stack>
      {dialog.messages.map((m, i) => (
        <Box key={i} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Chip size="small"
                  label={m.role === 'user' ? 'клиент' : 'ассистент'}
                  color={m.role === 'user' ? 'primary' : 'default'}/>
            <Typography variant="caption" color="text.secondary">
              {dayjs(m.date).format('DD.MM.YYYY HH:mm:ss')}
            </Typography>
            {m.outcome && outcomeChip(m.outcome)}
            {m.top1 != null &&
             <Typography variant="caption" color="text.secondary">
               top1 {m.top1.toFixed(3)}
             </Typography>}
            {m.latencyMs != null &&
             <Typography variant="caption" color="text.secondary">
               {m.latencyMs} мс
             </Typography>}
            {m.model &&
             <Typography variant="caption" color="text.secondary">{m.model}</Typography>}
            {m.limitHit &&
             <Chip size="small" color="error" label={'лимит: ' + m.limitHit}/>}
          </Stack>
          {m.attachments &&
           <Typography variant="caption" color="text.secondary" display="block">
             вложения: {m.attachments}
           </Typography>}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {m.message}
          </Typography>
        </Box>))}
    </Box>);
}

export default function NuiAdminConsoleChats() {
  const [dialogs, setDialogs] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [dateFrom, setDateFrom] = React.useState(dayjs(new Date()).subtract(30, 'day'));
  const [dateTo, setDateTo] = React.useState(dayjs(new Date()));
  const [inProgress, setInProgress] = React.useState(true);
  const [confirmDelete, setConfirmDelete] = React.useState(null);

  async function handleReload() {
    setInProgress(true);
    setSelected(null);
    // Верхняя граница у выборки полуоткрытая (create_date < to), поэтому за
    // выбранный день день прибавляется здесь - иначе сегодняшние диалоги
    // в отчёт не попадут.
    await axios.get('/api/support/chat/history/period', {
      params: { from: dateFrom.format('YYYY-MM-DD'),
                to: dateTo.add(1, 'day').format('YYYY-MM-DD') } })
      .then((response) => {
        setDialogs(toDialogs(response.data));
        setInProgress(false);
      })
      .catch((error) => {
        setDialogs([]);
        console.log(error);
        setInProgress(false);
      });
  }

  React.useEffect(() => {handleReload();}, []);

  // Удаление необратимо и ничего не архивирует: строки журнала уходят из базы
  // насовсем. Поэтому кнопка спрашивает подтверждение и называет, что именно
  // будет удалено.
  async function handleDelete(id) {
    setInProgress(true);
    // Play проверяет CSRF у запросов с сессионной кукой, а админ всегда с
    // ней: без этих заголовков DELETE не доходит до контроллера.
    await axios.delete('/api/support/chat/history/dialog/' + encodeURIComponent(id),
                       { headers: { 'X-Requested-With': 'HttpRequest',
                                    'Csrf-Token': 'nocheck' } })
      .then(() => {
        setDialogs((prev) => prev.filter((d) => d.id !== id));
        setSelected(null);
      })
      .catch((error) => console.log(error));
    setConfirmDelete(null);
    setInProgress(false);
  }

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
        <Box sx={{ height: '100%', width: '100%' }}>
          <Stack spacing={2} direction="row" sx={{ pb: 2 }}>
            <DatePicker label="Дата с"
              value={dateFrom}
              onChange={(newValue) => setDateFrom(newValue)}
              slotProps={{ textField: { variant: "standard" } }} />
            <DatePicker label="Дата по"
              value={dateTo}
              onChange={(newValue) => setDateTo(newValue)}
              slotProps={{ textField: { variant: "standard" } }} />
            <Button variant="contained" sx={{ height: '100%' }}
              onClick={handleReload}>
              Обновить
            </Button>
            <Typography variant="body2" color="text.secondary"
                        sx={{ alignSelf: 'center' }}>
              диалогов: {dialogs.length}
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
                 alignItems="stretch">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <DataGrid
                rows={dialogs}
                columns={columns}
                onRowClick={(params) => setSelected(params.row)}
                initialState={{
                  pagination: { paginationModel: { pageSize: 20 } },
                  sorting: { sortModel: [{ field: 'started', sort: 'desc' }] }
                }}
                pageSizeOptions={[20, 100]}
                isCellEditable={() => false}
                density="compact"
                loading={inProgress}
                sx={{ cursor: 'pointer' }}
                slotProps={{
                  loadingOverlay: {
                    variant: 'linear-progress',
                    noRowsVariant: 'linear-progress',
                  },
                }}
              />
            </Box>
            <Paper variant="outlined" sx={{ flex: 1, minWidth: 0 }}>
              <Transcript dialog={selected}
                          onDelete={() => setConfirmDelete(selected)}/>
            </Paper>
          </Stack>
          <Dialog open={confirmDelete != null}
                  onClose={() => setConfirmDelete(null)}>
            <DialogTitle>Удалить диалог?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {confirmDelete &&
                 `Из журнала уйдут все реплики диалога ${confirmDelete.id} ` +
                 `(${confirmDelete.turns} шт., от ` +
                 `${dayjs(confirmDelete.started).format('DD.MM.YYYY HH:mm')}). ` +
                 `Восстановить их будет нельзя.`}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDelete(null)}>Отмена</Button>
              <Button color="error" variant="contained"
                      onClick={() => handleDelete(confirmDelete.id)}>
                Удалить
              </Button>
            </DialogActions>
          </Dialog>
          <Button variant="contained"
                  sx={{ mt: 2, float: 'right' }}
                  onClick={logout(() => setInProgress(true),
                                  () => setInProgress(false))}>
            Выйти
          </Button>
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
